/**
 * RESEARCH ADMIN TAB
 * ====================
 * Dedicated UI for Super Admin to manage the strongly pseudonymized research dataset.
 * 
 * Features:
 * - Generate strongly pseudonymized dataset
 * - Validate k-anonymity
 * - Export CSV/JSON
 * - View data dictionary
 * - View audit logs
 */

import { useState, useEffect } from "react";
import api from "@/api";
import { SectionTabs } from "@/components/shared/SectionTabs";
import { toast } from "sonner";
import {
  FlaskConical, Database, Download, Shield, CheckCircle,
  AlertTriangle, FileText, Code, RefreshCw, Clock, User,
  FileSpreadsheet, FileJson, BookOpen, History, Lock
} from "lucide-react";

// Finalités de consentement recherche (codes = services/consent_service._RESEARCH_PURPOSES)
const CONSENT_PURPOSE_LABELS = {
  RESEARCH_ACADEMIC: "Recherche académique — RESEARCH_ACADEMIC",
  RESEARCH_PHARMA: "Export sponsor / laboratoire — RESEARCH_PHARMA",
};

// Nom de fichier fourni par le serveur (Content-Disposition) : porte la finalité du dataset (P1-2)
// Réponse d'erreur reçue en blob (responseType: 'blob') : extraire le `detail` serveur (ex. 409 verrou de finalité)
const exportErrorMessage = async (err, fallback) => {
  try {
    const data = err.response?.data;
    const text = data instanceof Blob ? await data.text() : JSON.stringify(data || {});
    return JSON.parse(text).detail || fallback;
  } catch { return fallback; }
};

const serverFilename = (res) => (res.headers?.["content-disposition"] || "").match(/filename=([^;]+)/)?.[1]?.trim();

export default function ResearchAdminTab() {
  const [status, setStatus] = useState(null);
  const [validation, setValidation] = useState(null);
  const [dictionary, setDictionary] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState({
    status: false,
    generate: false,
    validate: false,
    exportCsv: false,
    exportJson: false,
    dictionary: false,
    auditLogs: false,
  });
  const [activeSection, setActiveSection] = useState("overview");
  // P1-2 : finalité de consentement du dataset — choix explicite obligatoire (aucune présélection)
  const [consentPurpose, setConsentPurpose] = useState("");
  const [lastGeneration, setLastGeneration] = useState(null);

  // Fetch initial status
  useEffect(() => {
    fetchStatus();
    fetchAuditLogs();
  }, []);

  const fetchStatus = async () => {
    setLoading(l => ({ ...l, status: true }));
    try {
      const res = await api.get("/research/status");
      setStatus(res.data);
    } catch (err) {
      // error handled by toast
    } finally {
      setLoading(l => ({ ...l, status: false }));
    }
  };

  const fetchAuditLogs = async () => {
    setLoading(l => ({ ...l, auditLogs: true }));
    try {
      const res = await api.get("/research/audit-log?limit=20");
      setAuditLogs(res.data.logs || []);
    } catch (err) {
      // error handled by toast
    } finally {
      setLoading(l => ({ ...l, auditLogs: false }));
    }
  };

  const handleGenerate = async () => {
    if (!consentPurpose) {
      toast.error("Choisissez la finalité de consentement (académique ou sponsor) avant de générer le dataset.");
      return;
    }
    if (!confirm(`Voulez-vous générer un nouveau dataset de recherche (${CONSENT_PURPOSE_LABELS[consentPurpose]}) sous pseudonymisation forte ? Cette opération remplacera les données existantes.`)) {
      return;
    }
    
    setLoading(l => ({ ...l, generate: true }));
    try {
      const res = await api.post("/research/generate", null, { params: { consent_purpose: consentPurpose } });
      setLastGeneration({ purpose: consentPurpose, version: res.data.version, records: res.data.records_generated });
      toast.success(`Dataset généré: ${res.data.records_generated} enregistrements`);
      fetchStatus();
      fetchAuditLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setLoading(l => ({ ...l, generate: false }));
    }
  };

  const handleValidate = async () => {
    setLoading(l => ({ ...l, validate: true }));
    try {
      const res = await api.get("/research/validate");
      setValidation(res.data);
      if (res.data.status === "valid") {
        toast.success("Validation réussie: les contrôles de réduction du risque sont satisfaits");
      } else if (res.data.status === "no_data") {
        toast.warning("Aucun dataset à valider. Générez d'abord le dataset.");
      } else {
        toast.error("Validation échouée: des problèmes ont été détectés");
      }
    } catch (err) {
      toast.error("Erreur lors de la validation");
    } finally {
      setLoading(l => ({ ...l, validate: false }));
    }
  };

  const handleExportCsv = async () => {
    setLoading(l => ({ ...l, exportCsv: true }));
    try {
      const res = await api.get("/research/export/csv", {
        params: { consent_purpose: consentPurpose },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', serverFilename(res) || `hana_research_pseudonymized_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export CSV téléchargé");
      fetchAuditLogs();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Aucune donnée. Générez d'abord le dataset.");
      } else {
        toast.error(await exportErrorMessage(err, "Erreur lors de l'export CSV"));
      }
    } finally {
      setLoading(l => ({ ...l, exportCsv: false }));
    }
  };

  const handleExportJson = async () => {
    setLoading(l => ({ ...l, exportJson: true }));
    try {
      const res = await api.get("/research/export/json", {
        params: { consent_purpose: consentPurpose },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', serverFilename(res) || `hana_research_pseudonymized_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export JSON téléchargé");
      fetchAuditLogs();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Aucune donnée. Générez d'abord le dataset.");
      } else {
        toast.error(await exportErrorMessage(err, "Erreur lors de l'export JSON"));
      }
    } finally {
      setLoading(l => ({ ...l, exportJson: false }));
    }
  };

  const handleViewDictionary = async () => {
    setLoading(l => ({ ...l, dictionary: true }));
    try {
      const res = await api.get("/research/dictionary");
      setDictionary(res.data);
      setActiveSection("dictionary");
    } catch (err) {
      toast.error("Erreur lors du chargement du dictionnaire");
    } finally {
      setLoading(l => ({ ...l, dictionary: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-purple-600" />
            Administration Recherche
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Gestion du dataset sous pseudonymisation forte pour la recherche scientifique
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading.status}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          data-testid="refresh-research-status"
        >
          <RefreshCw className={`w-4 h-4 ${loading.status ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      {/* Security Notice */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-purple-800">Accès sécurisé - Super Admin uniquement</p>
          <p className="text-xs text-purple-600 mt-1">
            Toutes les actions sur ce module sont enregistrées dans un journal d'audit pour traçabilité RGPD/CNIL.
          </p>
        </div>
      </div>

      {/* Sub-navigation */}
      <SectionTabs
        variant="secondary"
        testIdPrefix="research-section"
        active={activeSection}
        onChange={setActiveSection}
        tabs={[
          { id: "overview", label: "Vue d'ensemble", icon: Database },
          { id: "validation", label: "Validation", icon: Shield },
          { id: "dictionary", label: "Dictionnaire", icon: BookOpen },
          { id: "audit", label: "Journal d'audit", icon: History },
        ]}
      />

      {/* Overview Section */}
      {activeSection === "overview" && (
        <div className="space-y-6">
          {/* Status Card */}
          <div className="dash-card">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Database className="w-4 h-4 text-purple-600" />
              État du dataset de recherche
            </h3>
            
            {loading.status ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : status?.status === "empty" ? (
              <div className="text-center py-8">
                <Database className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 mb-2">Aucun dataset généré</p>
                <p className="text-xs text-slate-400">Cliquez sur "Générer le dataset" pour créer les données sous pseudonymisation forte</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{status?.total_records || 0}</p>
                  <p className="text-sm text-slate-600">Enregistrements</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{status?.unique_combinations || 0}</p>
                  <p className="text-sm text-slate-600">Combinaisons uniques</p>
                </div>
                <div className={`rounded-xl p-4 text-center ${status?.k_anonymity_satisfied ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-3xl font-bold ${status?.k_anonymity_satisfied ? 'text-green-600' : 'text-red-600'}`}>
                    k={status?.minimum_k || 0}
                  </p>
                  <p className="text-sm text-slate-600">k-anonymity min</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-sm font-medium text-slate-700">
                    {status?.last_generated ? new Date(status.last_generated).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    }) : "—"}
                  </p>
                  <p className="text-sm text-slate-600">Dernière génération</p>
                </div>
              </div>
            )}
          </div>

          {/* Actions Card */}
          <div className="dash-card">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-purple-600" />
              Actions
            </h3>
            
            <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="research-consent-purpose-block">
              <label htmlFor="research-consent-purpose" className="text-sm font-medium text-slate-700">Finalité de consentement (génération et export)</label>
              <select
                id="research-consent-purpose"
                value={consentPurpose}
                onChange={(e) => setConsentPurpose(e.target.value)}
                data-testid="research-consent-purpose"
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">Sélectionner la finalité…</option>
                {Object.entries(CONSENT_PURPOSE_LABELS).map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
              {(lastGeneration || status?.version) && (
                <span className="text-xs text-slate-600" data-testid="research-dataset-consent-purpose">
                  Dataset courant : {CONSENT_PURPOSE_LABELS[lastGeneration?.purpose || status?.consent_purpose] || "sans finalité (non exportable — régénérer)"} · {lastGeneration?.version || status?.version}
                </span>
              )}
              {status?.withdrawals_since_generation > 0 && (
                <span className="text-xs text-amber-700" data-testid="research-dataset-withdrawals">
                  {status.withdrawals_since_generation} patient(s) ont retiré leur consentement depuis la génération : leurs données sont exclues automatiquement des exports et de la consultation.
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Generate */}
              <button
                onClick={handleGenerate}
                disabled={loading.generate || !consentPurpose}
                data-testid="generate-research-dataset"
                className="flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all disabled:opacity-50"
              >
                {loading.generate ? (
                  <RefreshCw className="w-8 h-8 animate-spin" />
                ) : (
                  <Database className="w-8 h-8" />
                )}
                <div className="text-center">
                  <p className="font-semibold">{loading.generate ? "Génération..." : "Générer le dataset"}</p>
                  <p className="text-xs text-purple-200 mt-1">Créer/Mettre à jour</p>
                </div>
              </button>

              {/* Validate */}
              <button
                onClick={handleValidate}
                disabled={loading.validate}
                data-testid="validate-research-dataset"
                className="flex flex-col items-center gap-3 p-6 bg-emerald-50 text-emerald-700 rounded-xl border-2 border-emerald-200 hover:bg-emerald-100 transition-all disabled:opacity-50"
              >
                {loading.validate ? (
                  <RefreshCw className="w-8 h-8 animate-spin" />
                ) : (
                  <Shield className="w-8 h-8" />
                )}
                <div className="text-center">
                  <p className="font-semibold">{loading.validate ? "Validation..." : "Valider"}</p>
                  <p className="text-xs text-emerald-600 mt-1">Vérifier k/l/t</p>
                </div>
              </button>

              {/* Export CSV */}
              <button
                onClick={handleExportCsv}
                disabled={loading.exportCsv || !consentPurpose}
                data-testid="export-research-csv"
                className="flex flex-col items-center gap-3 p-6 bg-blue-50 text-blue-700 rounded-xl border-2 border-blue-200 hover:bg-blue-100 transition-all disabled:opacity-50"
              >
                {loading.exportCsv ? (
                  <RefreshCw className="w-8 h-8 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-8 h-8" />
                )}
                <div className="text-center">
                  <p className="font-semibold">{loading.exportCsv ? "Export..." : "Export CSV"}</p>
                  <p className="text-xs text-blue-600 mt-1">Pour analyses statistiques</p>
                </div>
              </button>

              {/* Export JSON */}
              <button
                onClick={handleExportJson}
                disabled={loading.exportJson || !consentPurpose}
                data-testid="export-research-json"
                className="flex flex-col items-center gap-3 p-6 bg-amber-50 text-amber-700 rounded-xl border-2 border-amber-200 hover:bg-amber-100 transition-all disabled:opacity-50"
              >
                {loading.exportJson ? (
                  <RefreshCw className="w-8 h-8 animate-spin" />
                ) : (
                  <FileJson className="w-8 h-8" />
                )}
                <div className="text-center">
                  <p className="font-semibold">{loading.exportJson ? "Export..." : "Export JSON"}</p>
                  <p className="text-xs text-amber-600 mt-1">Avec métadonnées</p>
                </div>
              </button>
            </div>
          </div>

          {/* Top Combinations */}
          {status?.top_combinations && status.top_combinations.length > 0 && (
            <div className="dash-card">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-600" />
                Top 10 combinaisons (quasi-identifiants)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-slate-600">Tranche d'âge</th>
                      <th className="text-left p-3 font-medium text-slate-600">Type de cancer</th>
                      <th className="text-left p-3 font-medium text-slate-600">Traitement</th>
                      <th className="text-right p-3 font-medium text-slate-600">Effectif</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {status.top_combinations.map((combo, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-3 text-slate-700">{combo.age || "—"}</td>
                        <td className="p-3 text-slate-700">{combo.cancer || "—"}</td>
                        <td className="p-3 text-slate-700">{combo.treatment || "—"}</td>
                        <td className="p-3 text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            combo.count >= 5 ? "bg-green-100 text-green-700" :
                            combo.count >= 3 ? "bg-yellow-100 text-yellow-700" :
                            "bg-red-100 text-red-700"
                          }`}>
                            {combo.count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Validation Section */}
      {activeSection === "validation" && (
        <div className="space-y-6">
          <div className="dash-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-600" />
                Validation des contrôles privacy
              </h3>
              <button
                onClick={handleValidate}
                disabled={loading.validate}
                data-testid="run-research-validation-button"
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {loading.validate ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4" />
                )}
                Lancer la validation
              </button>
            </div>

            {!validation ? (
              <div className="text-center py-12 text-slate-400">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Cliquez sur "Lancer la validation" pour vérifier le dataset</p>
              </div>
            ) : validation.status === "no_data" ? (
              <div className="text-center py-12 text-amber-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3" />
                <p>Aucun dataset à valider</p>
                <p className="text-sm text-slate-500 mt-2">Générez d'abord le dataset dans la section "Vue d'ensemble"</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Overall Status */}
                <div className={`p-4 rounded-xl flex items-center gap-3 ${
                  validation.status === "valid" ? "bg-green-50" : "bg-red-50"
                }`}>
                  {validation.status === "valid" ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  )}
                  <div>
                    <p className={`font-semibold ${validation.status === "valid" ? "text-green-800" : "text-red-800"}`}>
                      {validation.status === "valid" ? "Dataset valide" : "Problèmes détectés"}
                    </p>
                    <p className="text-sm text-slate-600">{validation.recommendation}</p>
                  </div>
                </div>

                {/* Checks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(validation.checks || {}).map(([key, check]) => (
                    <div
                      key={key}
                      className={`p-4 rounded-xl border-2 ${
                        check.passed ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {check.passed ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        )}
                        <span className="font-medium text-slate-800">
                          {key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{check.details}</p>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-slate-700">{validation.total_records || 0}</p>
                    <p className="text-sm text-slate-500">Enregistrements</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-slate-700">{validation.unique_combinations || 0}</p>
                    <p className="text-sm text-slate-500">Combinaisons uniques</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dictionary Section */}
      {activeSection === "dictionary" && (
        <div className="space-y-6">
          <div className="dash-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-blue-600" />
                Dictionnaire des données
              </h3>
              <button
                onClick={handleViewDictionary}
                disabled={loading.dictionary}
                data-testid="load-research-dictionary-button"
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {loading.dictionary ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Charger
              </button>
            </div>

            {!dictionary ? (
              <div className="text-center py-12 text-slate-400">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Cliquez sur "Charger" pour voir le dictionnaire des données</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Dataset Info */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="font-semibold text-blue-800">{dictionary.dataset_name}</p>
                  <p className="text-sm text-blue-600">Version: {dictionary.version} | Niveau privacy: {dictionary.anonymization_level || dictionary.privacy_posture}</p>
                </div>

                {/* Fields by Category */}
                {Object.entries(dictionary.fields || {}).map(([category, fields]) => (
                  <div key={category} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 px-4 py-3 border-b">
                      <h4 className="font-medium text-slate-700 capitalize">{category}</h4>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {Object.entries(fields).map(([fieldName, fieldInfo]) => (
                        <div key={fieldName} className="p-4 hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div>
                              <code className="text-sm font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                                {fieldName}
                              </code>
                              <p className="text-sm text-slate-600 mt-2">{fieldInfo.description}</p>
                              {fieldInfo.type && (
                                <p className="text-xs text-slate-400 mt-1">Type: {fieldInfo.type}</p>
                              )}
                              {fieldInfo.values && (
                                <p className="text-xs text-slate-500 mt-1">
                                  Valeurs: {fieldInfo.values.slice(0, 5).join(", ")}
                                  {fieldInfo.values.length > 5 && "..."}
                                </p>
                              )}
                            </div>
                            {fieldInfo.anonymization && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                                Pseudonymisé
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Removed Fields */}
                {dictionary.removed_fields && (
                  <div className="border border-red-200 rounded-xl overflow-hidden">
                    <div className="bg-red-50 px-4 py-3 border-b">
                      <h4 className="font-medium text-red-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Champs supprimés (identifiants directs)
                      </h4>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {dictionary.removed_fields.map((field, idx) => (
                          <span key={idx} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Logs Section */}
      {activeSection === "audit" && (
        <div className="space-y-6">
          <div className="dash-card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <History className="w-4 h-4 text-slate-600" />
                Journal d'audit
              </h3>
              <button
                onClick={fetchAuditLogs}
                disabled={loading.auditLogs}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <RefreshCw className={`w-4 h-4 ${loading.auditLogs ? "animate-spin" : ""}`} />
                Actualiser
              </button>
            </div>

            {loading.auditLogs ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Aucune action enregistrée</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className={`p-2 rounded-full flex-shrink-0 ${
                      log.action?.includes("GENERATE") ? "bg-purple-100 text-purple-600" :
                      log.action?.includes("EXPORT") ? "bg-blue-100 text-blue-600" :
                      "bg-slate-200 text-slate-600"
                    }`}>
                      {log.action?.includes("GENERATE") ? (
                        <Database className="w-4 h-4" />
                      ) : log.action?.includes("EXPORT") ? (
                        <Download className="w-4 h-4" />
                      ) : (
                        <FileText className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800">{log.action}</span>
                        <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                          {log.user_role}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                        <User className="w-3 h-3" />
                        {log.user_email}
                      </p>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="text-xs text-slate-400 mt-2 bg-white rounded p-2">
                          {Object.entries(log.details).map(([k, v]) => (
                            <span key={k} className="mr-3">
                              <strong>{k}:</strong> {String(v)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {log.timestamp ? new Date(log.timestamp).toLocaleString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        }) : "—"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
